/**
 * Wave DOC (decision 0068, spec §4.4, §6.2): the contract between the app Worker's view route (F7) and the user-content
 * Worker (`apps/usercontent`). Pure and dependency-free (no `effect` import): exported as `@minga/protocol/doc-bundle`.
 *
 * F7 writes a manifest (canonical JSON) to R2 `bundles/<msha>.json`, `msha` = sha256 of those bytes, and answers a URL
 * `<USERCONTENT_ORIGIN>/b/<msha>/<exp>/<s>/<sig>/<entry>`. `sig` = base64url (no padding) of HMAC-SHA256 with
 * `USERCONTENT_SECRET` over {@link bundleSigInput}. `s` (scripts) is decided by the server; the user-content Worker
 * enforces it. The signature sits in the path, so the page's relative URLs (`./support.js`) keep it.
 */
import { normalizeDocPath } from "./doc-path"

/** One file a view may read: its sha256 (`s`), content type (`t`) and size in bytes (`n`). */
export interface BundleFile {
  readonly s: string
  readonly t: string
  readonly n: number
}

export interface BundleManifest {
  readonly v: 1
  /** Scripts run (the server's `viewOf`); the signed `s` must agree. */
  readonly scripts: boolean
  /** Path → bytes: the entry, support files (JS, CSS, fonts) and images under the entry's folder. */
  readonly files: Readonly<Record<string, BundleFile>>
  /** Path → `O-n`: other documents under the folder, served as "open in Minga" stubs, never as bytes. */
  readonly pages: Readonly<Record<string, string>>
}

/** A view URL lives one hour (F7); the web keeps it for that hour. */
export const VIEW_TTL_MS = 3_600_000
/** At most this many files (entry, support files, images) in one view. */
export const VIEW_MAX_FILES = 500

const HEX64 = /^[0-9a-f]{64}$/
/** base64url without padding of a 32-byte HMAC. */
const SIG = /^[A-Za-z0-9_-]{43}$/
const REF = /^O-[1-9]\d{0,8}$/

/** JSON with every object's keys sorted and no spaces: the exact bytes hashed and stored. */
const canonicalJson = (v: unknown): string => {
  if (v === null || typeof v !== "object") return JSON.stringify(v)
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(",")}]`
  const o = v as Record<string, unknown>
  const keys = Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(",")}}`
}

/** The manifest as canonical JSON (sorted keys at every level, no spaces). */
export const canonicalManifest = (m: BundleManifest): string => canonicalJson(m)

/** R2 key of a view manifest. */
export const bundleKey = (msha: string): string => `bundles/${msha}.json`
/** R2 key of an uploaded file (the same as the cloud's `files/routes.ts` `fileKey`). */
export const fileKey = (sha256: string): string => `files/${sha256}`

/** The bytes signed for a view URL. */
export const bundleSigInput = (msha: string, exp: number, s: 0 | 1): string => `v1\n${msha}\n${exp}\n${s}`

/** A path's segments, each percent-encoded (so spaces and non-ASCII survive the URL). */
const encodePath = (path: string): string => path.split("/").map(encodeURIComponent).join("/")

/** `/b/<msha>/<exp>/<s>/<sig>/<entry>`, the entry's segments percent-encoded. */
export const bundlePath = (p: {
  readonly msha: string
  readonly exp: number
  readonly s: 0 | 1
  readonly sig: string
  readonly entry: string
}): string => `/b/${p.msha}/${p.exp}/${p.s}/${p.sig}/${encodePath(p.entry)}`

export interface ParsedBundlePath {
  readonly msha: string
  readonly exp: number
  readonly s: 0 | 1
  readonly sig: string
  /** The rest of the pathname, still percent-encoded; {@link bundleEntryOf} turns it into a document path. */
  readonly rest: string
}

const BUNDLE_PATH = /^\/b\/([0-9a-f]{64})\/(\d{1,15})\/([01])\/([A-Za-z0-9_-]{43})\/(.+)$/

/** Parse a user-content pathname; null for anything that is not a well-formed bundle path. */
export const parseBundlePath = (pathname: string): ParsedBundlePath | null => {
  const m = BUNDLE_PATH.exec(pathname)
  if (!m) return null
  const [, msha, exp, s, sig, rest] = m
  if (msha === undefined || exp === undefined || sig === undefined || rest === undefined) return null
  return { msha, exp: Number(exp), s: s === "1" ? 1 : 0, sig, rest }
}

/**
 * The document path a bundle request names: each segment percent-decoded (a decoded "/" or "\\" is refused), then
 * {@link normalizeDocPath}. Null for traversal ("..", "%2e%2e"), bad encodings or anything else that is not a path.
 */
export const bundleEntryOf = (rest: string): string | null => {
  const segs: string[] = []
  for (const seg of rest.split("/")) {
    let d: string
    try {
      d = decodeURIComponent(seg)
    } catch {
      return null
    }
    if (d.includes("/") || d.includes("\\") || d === "..") return null
    segs.push(d)
  }
  const joined = segs.join("/")
  const path = normalizeDocPath(joined)
  // normalizing may only drop "." segments: a path it would change in any other way is refused
  return path !== null &&
    path ===
      joined
        .split("/")
        .filter((x) => x !== ".")
        .join("/")
    ? path
    : null
}

/** A manifest read back from R2: the shape checked, every sha 64 hex, every ref an `O-n`; else null. */
export const parseBundleManifest = (text: string): BundleManifest | null => {
  let v: unknown
  try {
    v = JSON.parse(text)
  } catch {
    return null
  }
  if (v === null || typeof v !== "object" || Array.isArray(v)) return null
  const m = v as Record<string, unknown>
  if (m.v !== 1 || typeof m.scripts !== "boolean") return null
  const files = m.files
  const pages = m.pages
  if (files === null || typeof files !== "object" || Array.isArray(files)) return null
  if (pages === null || typeof pages !== "object" || Array.isArray(pages)) return null
  for (const [p, f] of Object.entries(files as Record<string, unknown>)) {
    if (normalizeDocPath(p) !== p || f === null || typeof f !== "object") return null
    const { s, t, n } = f as Record<string, unknown>
    if (typeof s !== "string" || !HEX64.test(s) || typeof t !== "string" || typeof n !== "number") return null
    if (!Number.isSafeInteger(n) || n < 0) return null
  }
  for (const [p, ref] of Object.entries(pages as Record<string, unknown>))
    if (normalizeDocPath(p) !== p || typeof ref !== "string" || !REF.test(ref)) return null
  if (Object.keys(files).length > VIEW_MAX_FILES) return null
  return m as unknown as BundleManifest
}

/** Is `sig` shaped like a view signature (base64url, 43 characters)? */
export const isBundleSig = (sig: string): boolean => SIG.test(sig)

/** The CDN hosts a page may load scripts from (what Claude artifacts are written for; compatibility, not a fence). */
const SCRIPT_HOSTS =
  "https://cdnjs.cloudflare.com https://cdn.jsdelivr.net https://unpkg.com https://cdn.tailwindcss.com https://code.jquery.com"
const STYLE_HOSTS =
  "https://fonts.googleapis.com https://cdnjs.cloudflare.com https://cdn.jsdelivr.net https://unpkg.com"
const FONT_HOSTS = "https://fonts.gstatic.com https://cdnjs.cloudflare.com https://cdn.jsdelivr.net"

/** The policy every HTML view runs under, without `sandbox` and `frame-ancestors` (spec §6.2). */
const FRAME_POLICY = [
  "default-src 'none'",
  `script-src 'self' 'unsafe-inline' 'unsafe-eval' ${SCRIPT_HOSTS}`,
  `style-src 'self' 'unsafe-inline' ${STYLE_HOSTS}`,
  `font-src 'self' data: ${FONT_HOSTS}`,
  "img-src 'self' data: blob:",
  "media-src 'self' blob:",
  "connect-src 'self'",
  "worker-src blob:",
  "form-action 'none'",
  "base-uri 'self'",
  "frame-src 'none'",
]

/**
 * The `Content-Security-Policy` header of an HTML document the user-content Worker serves (spec §6.2): `sandbox
 * allow-scripts` (or `sandbox` alone when scripts are off), the policy, `frame-ancestors <appOrigin>`.
 */
export const DOC_FRAME_CSP = (appOrigin: string, scripts: boolean): string =>
  [scripts ? "sandbox allow-scripts" : "sandbox", ...FRAME_POLICY, `frame-ancestors ${appOrigin}`].join("; ")

/**
 * The iframe's `csp` attribute (CSP Embedded Enforcement, spec §5.3): the same policy without `sandbox` (the iframe's
 * own `sandbox` attribute does that) and without `frame-ancestors` (it names the embedder, which a required policy
 * cannot). Any response in the frame must send `Allow-CSP-From: <APP_ORIGIN>` or Chrome blocks it.
 */
export const DOC_FRAME_REQUIRED_CSP: string = FRAME_POLICY.join("; ")

/** The message a page stub posts to the app (`{minga: "open", ref}`), to the app origin only. */
export const DOC_OPEN_MESSAGE = "open"
export interface DocOpenMessage {
  readonly minga: typeof DOC_OPEN_MESSAGE
  readonly ref: string
}
/** Is `data` (a `message` event's data) a page stub's open request? */
export const isDocOpenMessage = (data: unknown): data is DocOpenMessage => {
  if (data === null || typeof data !== "object") return false
  const d = data as Record<string, unknown>
  return d.minga === DOC_OPEN_MESSAGE && typeof d.ref === "string" && REF.test(d.ref)
}
